import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Button, Nav, Modal, Form, Badge } from 'react-bootstrap';
import {
  BookOpen, Mail, LogOut, Plus, Pencil, Trash2, Eye, Bell, User, ChevronLeft, ChevronRight, ClipboardList,
  Clock, ArrowRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from "jwt-decode";
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('courses');
  const [showModal, setShowModal] = useState(false);
  const [courses, setCourses] = useState([]);
  const [messages, setMessages] = useState([]);
  const [adminEmail, setAdminEmail] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseImage, setCourseImage] = useState(null);
  const [showViewCourseModal, setShowViewCourseModal] = useState(false);
  const [courseRequests, setCourseRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);

  // --- State for messages ---
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);

  // --- Stay updated ---
  const [news, setNews] = useState([]);
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', content: '' });
  const [newsImage, setNewsImage] = useState(null);
  const [selectedNews, setSelectedNews] = useState(null);

  const [pendingNews, setPendingNews] = useState([]);
  const [showViewNewsModal, setShowViewNewsModal] = useState(false);

  const token = sessionStorage.getItem('adminToken');
  const navigate = useNavigate();

  // Course field
  const [newCourse, setNewCourse] = useState({
    title: '',
    titleJp: '',
    // duration: '',
    description: '',
    descriptionJp: ''
  });

  useEffect(() => {
    if (!token) {
      navigate('/admin-portal-access');
    } else {
      try {
        const decoded = jwtDecode(token);
        setAdminEmail(decoded.email);
      } catch (error) {
        navigate('/admin-portal-access');
      }
    }
  }, [navigate, token]);

  const fetchCourses = async () => {
    try {
      //const response = await fetch('http://localhost:5000/api-portal/courses');
      const response = await fetch('/api-portal/courses');

      // If the server is restarted (401), logout user
      if (response.status === 401) {
        handleLogout();
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setCourses(data);
      }
    } catch (error) { console.error(error); }
  };

  const fetchMessages = async () => {
    try {
      //const response = await fetch('http://localhost:5000/api-portal/admin/messages', {
      const response = await fetch('/api-portal/admin/messages', {
        headers: { 'Authorization': token }
      });

      // 401 for end of session
      if (response.status === 401) {
        handleLogout();
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) { console.error(error); }
  };

  const handleDeleteMessage = async (id) => {
    if (window.confirm("Are you sure you want to delete this message?")) {
      try {
        //const response = await fetch(`http://localhost:5000/api-portal/admin/messages/${id}`, {
        const response = await fetch(`/api-portal/admin/messages/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': token }
        });
        if (response.ok) fetchMessages();
      } catch (error) { console.error("Error deleting message:", error); }
    }
  };

  const handleSaveCourse = async (e) => {
    e.preventDefault();

    // Using FormData because we send file
    const formData = new FormData();
    formData.append('title', newCourse.title || '');
    formData.append('titleJp', newCourse.titleJp || '');
    // formData.append('duration', newCourse.duration || '');
    formData.append('description', newCourse.description || '');
    formData.append('descriptionJp', newCourse.descriptionJp || '');
    if (courseImage) {
      formData.append('courseImage', courseImage);
    }

    const url = isEditMode
      // ? `http://localhost:5000/api-portal/courses/${selectedCourse.id}`
      // : 'http://localhost:5000/api-portal/courses';

      ? `/api-portal/courses/${selectedCourse.id}`
      : '/api-portal/courses';

    const method = isEditMode ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': token
        },
        body: formData
      });

      if (response.ok) {
        setShowModal(false);
        setNewCourse({ title: '', titleJp: '', description: '', descriptionJp: '' }); //duration: ''
        setCourseImage(null); // reset picture
        fetchCourses();
      }
    } catch (error) { console.error("Error saving course:", error); }
  };

  const fetchCourseRequests = async () => {
    try {
      //const response = await fetch('http://localhost:5000/api-portal/admin/course-requests', {
      const response = await fetch('/api-portal/admin/course-requests', {
        headers: { 'Authorization': token }
      });
      if (response.status === 401) return handleLogout();
      if (response.ok) {
        const data = await response.json();
        setCourseRequests(data);
      }
    } catch (error) { console.error(error); }
  };

  const handleDeleteCourse = async (id) => {
    if (window.confirm("Delete this course?")) {
      try {
        //await fetch(`http://localhost:5000/api-portal/courses/${id}`, {
        await fetch(`/api-portal/courses/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': token }
        });
        fetchCourses();
      } catch (error) { console.error("Error deleting course:", error); }
    }
  };

  const handleDeleteRequest = async (id) => {
    if (window.confirm("Are you sure you want to delete this course request?")) {
      try {
        //const response = await fetch(`http://localhost:5000/api-portal/admin/course-requests/${id}`, {
        const response = await fetch(`/api-portal/admin/course-requests/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': token }
        });

        if (response.status === 401) return handleLogout();

        if (response.ok) {
          // Refresh list
          fetchCourseRequests();
        }
      } catch (error) {
        console.error("Error deleting course request:", error);
      }
    }
  };

  useEffect(() => {
    if (activeTab === 'courses') fetchCourses();
    if (activeTab === 'messages') fetchMessages();
    if (activeTab === 'course-requests') fetchCourseRequests();
    if (activeTab === 'updates') fetchNews();
  }, [activeTab]);

  const handleLogout = () => {
    sessionStorage.removeItem('adminToken');
    navigate('/admin-portal-access');
  };

  const getTitle = () => {
    switch (activeTab) {
      case 'courses': return 'Course Management';
      case 'course-requests': return 'Course Messages';
      case 'messages': return 'Inbox Messages';
      case 'pending-news': return 'News Moderation (Pending)';
      case 'updates': return 'Stay Updated List';
      default: return 'Dashboard';
    }
  };

  const fetchNews = async () => {
    try {
      //const response = await fetch('http://localhost:5000/api-portal/news');
      const response = await fetch('/api-portal/news');
      if (response.ok) {
        const data = await response.json();
        setNews(data);
      }
    } catch (error) { console.error(error); }
  };

  // Fun. for removing news
  const handleDeleteNews = async (id) => {
    if (window.confirm("Are you sure you want to delete this news post?")) {
      try {
        //const response = await fetch(`http://localhost:5000/api-portal/news/${id}`, {
        const response = await fetch(`/api-portal/news/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': token }
        });

        if (response.status === 401) return handleLogout();

        if (response.ok) {
          fetchNews(); // Refresh list
        }
      } catch (error) {
        console.error("Error deleting news:", error);
      }
    }
  };

  // Fun. for saving new news
  const handleSaveNews = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('title', newPost.title);
    formData.append('content', newPost.content);
    if (newsImage) {
      formData.append('newsImage', newsImage);
    }

    // Decideing URL and method depends on mode (edit/new)
    const url = isEditMode
      // ? `http://localhost:5000/api-portal/news/${selectedNews.id}`
      // : 'http://localhost:5000/api-portal/news';

      ? `/api-portal/news/${selectedNews.id}`
      : '/api-portal/news';
    const method = isEditMode ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method: method,
        headers: { 'Authorization': token },
        body: formData
      });

      if (response.status === 401) return handleLogout();

      if (response.ok) {
        setShowNewsModal(false);
        setNewPost({ title: '', content: '' });
        setNewsImage(null);
        setIsEditMode(false); // Reset mod
        fetchNews();
      }
    } catch (error) {
      console.error("Error saving news:", error);
    }
  };

  const fetchPendingNews = async () => {
    try {
      //const response = await fetch('http://localhost:5000/api-portal/news-pending', {
      const response = await fetch('/api-portal/news-pending', {
        headers: { 'Authorization': token }
      });
      if (response.ok) setPendingNews(await response.json());
    } catch (error) { console.error(error); }
  };

  // Dodaj u useEffect koji prati activeTab
  if (activeTab === 'pending-news') fetchPendingNews();

  // Funkcija za Accept
  const handleAcceptNews = async (id) => {
    //const response = await fetch(`http://localhost:5000/api-portal/news-accept/${id}`, {
    const response = await fetch(`/api-portal/news-accept/${id}`, {
      method: 'POST',
      headers: { 'Authorization': token }
    });
    if (response.ok) fetchPendingNews();
  };

  const handleDeletePending = async (id) => {
    if (window.confirm("Are you sure you want to discard this news suggestion?")) {
      try {
        //const response = await fetch(`http://localhost:5000/api-portal/news-pending/${id}`, {
        const response = await fetch(`/api-portal/news-pending/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': token }
        });

        if (response.status === 401) return handleLogout();

        if (response.ok) {
          fetchPendingNews(); // Osveži tabelu
        }
      } catch (error) {
        console.error("Error deleting pending news:", error);
      }
    }
  };

  // 1. State for current page
  const [currentPage, setCurrentPage] = useState(1);

  // 2. Reset to first page when changeing tab
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // 3. Define the limit based of active tab
  const itemsPerPage = activeTab === 'courses' ? 16 : 10;

  // 4. Calculation for showing
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  // 5. Cut data for table
  const currentCourses = courses.slice(indexOfFirstItem, indexOfLastItem);
  const currentMessages = messages.slice(indexOfFirstItem, indexOfLastItem);
  const currentCourseRequests = courseRequests.slice(indexOfFirstItem, indexOfLastItem);
  const currentNews = news.slice(indexOfFirstItem, indexOfLastItem);
  const currentPendingNews = pendingNews.slice(indexOfFirstItem, indexOfLastItem);

  // 6. Function for rendering pagination (universal)
  const renderPagination = (totalItems) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    if (totalPages <= 1) return null;

    return (
      <div className="pagination-wrapper d-flex justify-content-center mt-4">
        <Button
          variant="light"
          className="me-2 ad-page-btn"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(prev => prev - 1)}
        >
          <ChevronLeft size={16} />
        </Button>

        {[...Array(totalPages)].map((_, index) => (
          <Button
            key={index + 1}
            className={`me-2 ad-page-btn ${currentPage === index + 1 ? 'ad-page-active' : ''}`}
            onClick={() => setCurrentPage(index + 1)}
          >
            {index + 1}
          </Button>
        ))}

        <Button
          variant="light"
          className="ad-page-btn"
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(prev => prev + 1)}
        >
          <ChevronRight size={16} />
        </Button>
      </div>
    );
  };


  return (
    <div className="admin-dashboard-wrapper">
      <Row className="g-0">
        {/* Sidebar */}
        <Col md={2} className="admin-sidebar d-flex flex-column">
          <div className="sidebar-header">
            <h4 className="text-white fw-bold">LEVEL 9 <span className="blue-text">CO.</span> portal</h4>
          </div>
          <Nav className="flex-column mt-4 px-3">
            <Nav.Link className={`sidebar-link ${activeTab === 'courses' ? 'ad-active-link' : ''}`} onClick={() => setActiveTab('courses')}>
              <BookOpen size={18} className="me-2" /> Courses
            </Nav.Link>
            <Nav.Link className={`sidebar-link ${activeTab === 'course-requests' ? 'ad-active-link' : ''}`} onClick={() => setActiveTab('course-requests')}>
              <ClipboardList size={18} className="me-2" /> Course Requests
            </Nav.Link>
            <Nav.Link className={`sidebar-link ${activeTab === 'messages' ? 'ad-active-link' : ''}`} onClick={() => setActiveTab('messages')}>
              <Mail size={18} className="me-2" /> Received Mail
            </Nav.Link>
            {/* <Nav.Link className={`sidebar-link ${activeTab === 'pending-news' ? 'ad-active-link' : ''}`} onClick={() => setActiveTab('pending-news')}>
              <Clock size={18} className="me-2" /> News Pending
            </Nav.Link>
            <Nav.Link className={`sidebar-link ${activeTab === 'updates' ? 'ad-active-link' : ''}`} onClick={() => setActiveTab('updates')}>
              <Bell size={18} className="me-2" /> Stay Updated
            </Nav.Link> */}
          </Nav>

          <div className="sidebar-footer p-4 mt-auto">
            <div className="user-box mb-4">
              <small className="d-block text-white">Active Admin</small>
              <strong className="text-white truncate d-block">{adminEmail}</strong>
            </div>
            <Button variant="outline-danger" size="sm" className="w-100" onClick={handleLogout}>
              <LogOut size={14} className="me-2" /> Logout
            </Button>
          </div>
        </Col>

        {/* MAIN CONTENT */}
        <Col md={10} className="admin-main-content">
          <Container fluid className="p-5">

            <div className="d-flex justify-content-between align-items-center mb-2">
              <h2 className="dashboard-title">{getTitle()}</h2>

              {/* Button for courses */}
              {activeTab === 'courses' && (
                <Button className="post-course-btn" onClick={() => { setIsEditMode(false); setShowModal(true); }}>
                  <Plus size={18} className="me-2" /> Post New Course
                </Button>
              )}

              {/* Button for news */}
              {activeTab === 'updates' && (
                <Button className="post-news-btn" onClick={() => { setIsEditMode(false); setShowNewsModal(true); }}>
                  <Plus size={18} className="me-2" /> Post News
                </Button>
              )}
            </div>

            {/* Pagination summary text*/}
            <div className="results-summary mb-4">
              <p className="text-muted small">
                {activeTab === 'courses' && (
                  <>Page <strong>{currentPage}</strong> showing <strong>{courses.length === 0 ? 0 : indexOfFirstItem + 1}-{Math.min(indexOfLastItem, courses.length)}</strong> of <strong>{courses.length}</strong> Courses</>
                )}
                {activeTab === 'course-requests' && (
                  <>Page <strong>{currentPage}</strong> showing <strong>{courseRequests.length === 0 ? 0 : indexOfFirstItem + 1}-{Math.min(indexOfLastItem, courseRequests.length)}</strong> of <strong>{courseRequests.length}</strong> Requests</>
                )}
                {activeTab === 'messages' && (
                  <>Page <strong>{currentPage}</strong> showing <strong>{messages.length === 0 ? 0 : indexOfFirstItem + 1}-{Math.min(indexOfLastItem, messages.length)}</strong> of <strong>{messages.length}</strong> Messages</>
                )}
                {activeTab === 'pending-news' && (
                  <>Page <strong>{currentPage}</strong> showing <strong>{pendingNews.length === 0 ? 0 : indexOfFirstItem + 1}-{Math.min(indexOfLastItem, pendingNews.length)}</strong> of <strong>{pendingNews.length}</strong> Pending Suggestions</>
                )}
                {activeTab === 'updates' && (
                  <>Page <strong>{currentPage}</strong> showing <strong>{news.length === 0 ? 0 : indexOfFirstItem + 1}-{Math.min(indexOfLastItem, news.length)}</strong> of <strong>{news.length}</strong> News Posts</>
                )}
              </p>
            </div>

            {/* Table: Courses -------------------------------------------*/}
            {activeTab === 'courses' && (
              <div className="white-table-card animate-in">
                <Table responsive hover className="admin-table">
                  <thead>
                    <tr>
                      <th>Course Name</th>
                      {/* <th>Duration</th> */}
                      <th>Date Added</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentCourses.map(c => (
                      <tr key={c.id}>
                        <td className="fw-bold text-dark">{c.title}</td>
                        {/* <td>{c.duration}</td> */}
                        <td>{new Date(c.createdAt).toLocaleDateString()}</td>
                        <td>
                          <Button
                            variant="outline-info"
                            size="sm"
                            className="me-2"
                            onClick={() => {
                              setSelectedCourse(c);
                              setShowViewCourseModal(true);
                            }}
                          >
                            <Eye size={12} /> View
                          </Button>
                          <Button variant="outline-primary" size="sm" className="me-2" onClick={() => {
                            setIsEditMode(true);
                            setSelectedCourse(c);
                            setNewCourse(c);
                            setShowModal(true);
                          }}>
                            <Pencil size={12} /> Edit
                          </Button>

                          <Button variant="outline-danger" size="sm" onClick={() => handleDeleteCourse(c.id)}>
                            <Trash2 size={12} /> Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {renderPagination(courses.length)}
              </div>
            )}

            {/* Table: Course requests -----------------------------------------*/}
            {activeTab === 'course-requests' && (
              <div className="white-table-card animate-in">
                <Table responsive hover className="admin-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Course</th>
                      <th>Contact</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentCourseRequests.length > 0 ? currentCourseRequests.map(r => (
                      <tr key={r.id}>
                        <td><strong>{r.name}</strong></td>
                        <td>{r.courseTitle}</td>
                        <td>{r.email}</td>
                        <td>{new Date(r.createdAt).toLocaleDateString()}</td>
                        <td>
                          <Button variant="outline-info" size="sm" className="me-2" onClick={() => { setSelectedRequest(r); setShowRequestModal(true); }}>
                            <Eye size={12} /> View
                          </Button>
                          <Button variant="outline-danger" size="sm" onClick={() => handleDeleteRequest(r.id)}>
                            <Trash2 size={12} /> Delete
                          </Button>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan="5" className="text-center py-4">No requests yet.</td></tr>
                    )}
                  </tbody>
                </Table>
                {renderPagination(courseRequests.length)}
              </div>
            )}

            {/* Table: Messages */}
            {activeTab === 'messages' && (
              <div className="white-table-card animate-in">
                <Table responsive hover className="admin-table">
                  <thead>
                    <tr>
                      <th>From</th>
                      <th>Email</th>
                      {/* <th>Subject</th> */}
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentMessages.length > 0 ? currentMessages.map(m => (
                      <tr key={m.id}>
                        <td><strong>{m.name}</strong></td>
                        <td>{m.email}</td>
                        {/* <td>{m.subject}</td> */}
                        <td>{new Date(m.createdAt).toLocaleDateString()}</td>
                        <td>
                          <Button variant="outline-info" size="sm" className="me-2" onClick={() => {
                            setSelectedMessage(m);
                            setShowMessageModal(true);
                          }}>
                            <Eye size={12} /> View
                          </Button>

                          <Button variant="outline-danger" size="sm" onClick={() => handleDeleteMessage(m.id)}>
                            <Trash2 size={12} /> Delete
                          </Button>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan="5" className="text-center py-4">No messages received yet.</td></tr>
                    )}
                  </tbody>
                </Table>
                {renderPagination(messages.length)}
              </div>
            )}

            {/* Tab: Updates ------------------------------------------------------------------*/}
            {/* {activeTab === 'updates' && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="text-muted m-0">News Feed</h5>
                </div>
                <div className="white-table-card animate-in">
                  <Table responsive hover className="admin-table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentNews.map(n => (
                        <tr key={n.id}>
                          <td className="fw-bold">{n.title}</td>
                          <td>{new Date(n.createdAt).toLocaleDateString()}</td>
                          <td> */}
                            {/* Edit button --------*/}
                            {/* <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-2"
                              onClick={() => {
                                setIsEditMode(true);
                                setSelectedNews(n);
                                setNewPost({ title: n.title, content: n.content });
                                setShowNewsModal(true);
                              }}
                            >
                              <Pencil size={12} /> Edit
                            </Button>

                            <Button variant="outline-danger" size="sm" onClick={() => handleDeleteNews(n.id)}>
                              <Trash2 size={12} /> Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  {renderPagination(news.length)}
                </div>
              </>
            )} */}

            {/* {activeTab === 'pending-news' && (
              <div className="white-table-card animate-in mt-4">
                <Table responsive hover className="admin-table">
                  <thead>
                    <tr>
                      <th>Source/Title</th> */}
                      {/* <th>Date</th> Comment out*/}
                      {/* <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPendingNews.length > 0 ? currentPendingNews.map(n => (
                      <tr key={n.id}>
                        <td>
                          <Badge bg="info" className="me-2">Feed</Badge>
                          <strong>{n.title}</strong>
                        </td> */}
                        {/* <td>{selectedNews && new Date(selectedNews.createdAt).toLocaleString()}</td> Comment out this*/}
                        {/* <td>
                          <Button variant="outline-success" size="sm" className="me-2" onClick={() => handleAcceptNews(n.id)}>
                            Accept
                          </Button>
                          <Button variant="outline-info" size="sm" className="me-2" onClick={() => {
                            setSelectedNews(n);
                            setShowViewNewsModal(true);
                          }}>
                            <Eye size={12} /> View
                          </Button>
                          <Button variant="outline-danger" size="sm" onClick={() => handleDeletePending(n.id)}>
                            Delete
                          </Button>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan="2" className="text-center py-4">No new feed items.</td></tr>
                    )}
                  </tbody>
                </Table>
                {renderPagination(pendingNews.length)}
              </div>
            )} */}
          </Container>
        </Col>
      </Row>

      {/* Course modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>{isEditMode ? 'Edit Course' : 'Add New Course'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSaveCourse}>
            {/* --- Title section --------------------------------------------------- */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Course Title (EN)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Information Security"
                    value={newCourse.title}
                    onChange={(e) => setNewCourse({ ...newCourse, title: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold text-danger">Course Title (JP)</Form.Label>
                  <Form.Control
                    type="text"
                    value={newCourse.titleJp}
                    onChange={(e) => setNewCourse({ ...newCourse, titleJp: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              {/* <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Duration</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. 40 Hours"
                    value={newCourse.duration}
                    onChange={(e) => setNewCourse({ ...newCourse, duration: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col> */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Course Image</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e) => setCourseImage(e.target.files[0])}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* --- DESCRIPTION SECTION --- */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Description (EN)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newCourse.description}
                onChange={(e) => setNewCourse({ ...newCourse, description: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold text-danger">Description (JP)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newCourse.descriptionJp}
                onChange={(e) => setNewCourse({ ...newCourse, descriptionJp: e.target.value })}
              />
            </Form.Group>

            <div className="pt-3 border-top text-end">
              <Button variant="danger" className="me-2" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" variant="dark" className="add-btn-blue px-4">Save Course</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Course request Modal --------------------------------------------------------------------*/}
      <Modal show={showRequestModal} onHide={() => setShowRequestModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>Course Request Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="p-2">
            <Row className="mb-4">
              <Col md={6}>
                <small className="text-red text-uppercase fw-bold d-block mb-1">Student Information</small>
                <h5 className="fw-bold">Student: {selectedRequest?.name}</h5>
                <p className="mb-0 text-dark">Email: {selectedRequest?.email}</p>
                <p className="text-dark">Contact: {selectedRequest?.phone}</p>

              </Col>
              <Col md={6} className="text-md-end">
                <small className="text-red text-uppercase fw-bold d-block mb-1">Applied For</small>
                <div>
                  {selectedRequest?.courseTitle}
                </div>
                <p>Received on: {selectedRequest && new Date(selectedRequest.createdAt).toLocaleString()}</p>
              </Col>
            </Row>
            <hr />

            <div className="mt-4">
              <small className="text-muted text-uppercase fw-bold d-block mb-2">Student's Message</small>
              <div className="message-content-box p-3 bg-light border border-2 border-dark" style={{ whiteSpace: 'pre-wrap', minHeight: '150px' }}>
                {selectedRequest?.message || "No message provided."}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="dark" className="px-4" onClick={() => setShowRequestModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Course view Modal -----------------------------------------------------------------------------*/}
      <Modal show={showViewCourseModal} onHide={() => setShowViewCourseModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Course Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {selectedCourse?.image ? (
            <img
              //src={`http://localhost:5000/api-portal/uploads/courses/${selectedCourse.image}`}
              src={`/api-portal/uploads/courses/${selectedCourse.image}`}
              alt="Course"
              className="img-fluid rounded mb-3 shadow-sm"
              style={{ maxHeight: '350px', objectFit: 'cover' }}
            />
          ) : (
            <div className="bg-light rounded mb-3 d-flex align-items-center justify-content-center" style={{ height: '200px' }}>
              <span>No image uploaded</span>
            </div>
          )}

          <h3 className="fw-bold text-dark">{"Title: " + selectedCourse?.title}</h3>
          <h5 className="text-danger mb-3">{selectedCourse?.titleJp}</h5>

          {/* <div className="fw-bold">{selectedCourse?.duration}</div> */}

          <Row className="text-start mt-3">
            <Col md={6}>
              <h6 className="fw-bold border-bottom pb-2">Description (EN)</h6>
              <p style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                {selectedCourse?.description || "No English description provided."}
              </p>
            </Col>
            <Col md={6}>
              <h6 className="fw-bold border-bottom pb-2 text-danger">Description (JP)</h6>
              <p style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                {selectedCourse?.descriptionJp || "日本語の説明はありません"}
              </p>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="dark" onClick={() => setShowViewCourseModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Message Modal --------------------------------------------------------------------------*/}
      <Modal show={showMessageModal} onHide={() => setShowMessageModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Message from {selectedMessage?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p><strong>Email:</strong> {selectedMessage?.email}</p>
          {/* <p><strong>Subject:</strong> {selectedMessage?.subject}</p> */}
          <hr />
          <p style={{ whiteSpace: 'pre-wrap' }}>{selectedMessage?.message}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="dark" onClick={() => setShowMessageModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* News Modal ------------------------------------------------------------------------*/}
      <Modal show={showNewsModal} onHide={() => setShowNewsModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>Post Latest News</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSaveNews}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">News Title</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter title..."
                value={newPost.title}
                onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">News Image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e) => setNewsImage(e.target.files[0])}
              />
              <small className="text-muted">Optional: Upload a cover photo for this post.</small>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Content</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                placeholder="Write your news article here..."
                value={newPost.content}
                onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                required
              />
            </Form.Group>

            <div className="text-end pt-3 border-top">
              <Button variant="danger" className="me-2" onClick={() => setShowNewsModal(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="dark" className="px-4">
                Publish News
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* News pending modal */}
      <Modal show={showViewNewsModal} onHide={() => setShowViewNewsModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-dark text-white">
          <Modal.Title>News Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {/* Show source if it's auto news */}
          {selectedNews?.isAutomated && (
            <Badge bg="warning" text="dark" className="mb-3 text-uppercase">
              Suggested by bot (JPCERT/Security Next)
            </Badge>
          )}

          <h2 className="fw-bold mb-3">{selectedNews?.title}</h2>

          <div className="d-flex align-items-center text-muted mb-4">
            <Clock size={16} className="me-2" />
            <span>Detected on: {selectedNews && new Date(selectedNews.createdAt).toLocaleString()}</span>
          </div>

          {/* Picture of news (if exists) */}
          {selectedNews?.image && (
            <img
              //src={`http://localhost:5000/api-portal/uploads/news/${selectedNews.image}`}
              src={`/api-portal/uploads/news/${selectedNews.image}`}
              alt="News"
              className="img-fluid rounded mb-4 shadow-sm w-100"
              style={{ maxHeight: '300px', objectFit: 'cover' }}
            />
          )}

          <div className="news-content-preview p-3 bg-light border border-2 border-dark" style={{ whiteSpace: 'pre-wrap', minHeight: '200px' }}>
            {selectedNews?.content || "No content available."}
          </div>

          {/* Original link */}
          {selectedNews?.link && (
            <div className="mt-4 pt-3 border-top text-end">
              <a href={selectedNews.link} target="_blank" rel="noopener noreferrer" className="btn btn-outline-dark btn-sm">
                Read Original Article <ArrowRight size={14} />
              </a>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {/* If we are in pending tab, add quick action inside modal */}
          {activeTab === 'pending-news' && (
            <>
              <Button variant="danger" onClick={() => { handleDeletePending(selectedNews.id); setShowViewNewsModal(false); }}>
                Discard Suggestion
              </Button>
              <Button variant="success" onClick={() => { handleAcceptNews(selectedNews.id); setShowViewNewsModal(false); }}>
                Accept & Publish
              </Button>
            </>
          )}
          <Button variant="dark" onClick={() => setShowViewNewsModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminDashboard;